import { useCallback, useState } from 'react';
import type { ZodSchema } from 'zod';

export type FieldErrors = Record<string, string>;

/**
 * Validate `data` against a Zod schema, returning either the parsed value or a
 * flat field->message error map suitable for MUI TextField `error`/`helperText`.
 */
export function validate<T>(
  schema: ZodSchema<T>,
  data: unknown,
): { success: true; data: T } | { success: false; errors: FieldErrors } {
  const result = schema.safeParse(data);
  if (result.success) return { success: true, data: result.data };
  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.') || '_';
    if (!errors[key]) errors[key] = issue.message;
  }
  return { success: false, errors };
}

/**
 * Minimal validate-on-submit hook. Avoids pulling in a full form library while
 * giving consistent validation + error surfacing across the dashboard.
 *
 *   const { errors, validateForm, clearError } = useZodForm(schema);
 *   const onSubmit = () => { const parsed = validateForm(formState); if (!parsed) return; ...use parsed }
 */
export function useZodForm<T>(schema: ZodSchema<T>) {
  const [errors, setErrors] = useState<FieldErrors>({});

  const validateForm = useCallback(
    (data: unknown): T | null => {
      const res = validate(schema, data);
      if (res.success) {
        setErrors({});
        return res.data;
      }
      setErrors(res.errors);
      return null;
    },
    [schema],
  );

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const reset = useCallback(() => setErrors({}), []);

  return { errors, setErrors, validateForm, clearError, reset };
}
